import { useEffect, useRef } from 'react';

/**
 * Focus containment and restoration for a modal dialog. Without it, Tab
 * walks out of an open dialog into the page behind, and closing leaves focus
 * on `<body>` — a keyboard user loses their place.
 *
 * What the hook does, and why each part is not optional:
 *  - moves focus INTO the dialog on open, so the next Tab starts inside it;
 *  - cycles Tab and Shift+Tab within the dialog (WAI-ARIA « Dialog (Modal) »);
 *  - RESTORES focus to the element that opened it, so ⌘K → Escape returns the
 *    caret where it was.
 *
 * `role="dialog"`, `aria-modal="true"` and the label stay the caller's job —
 * they belong on its own markup, and a hook that injected them would hide
 * from the reader what the DOM actually declares.
 *
 * One hooked dialog open at a time: each open hook listens on `document`,
 * and two would both receive every key.
 */

/** Elements that can hold focus. `[hidden]` and negative tabindex excluded. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    // A tab stop the user cannot see must not be one they can reach.
    // `hidden` and `inert` are checked directly rather than through
    // `offsetParent`, which is null for EVERY element under jsdom and would
    // make the trap silently empty in tests while working in a browser.
    if (el.hasAttribute('hidden') || el.closest('[hidden]')) return false;
    if (el.closest('[inert]') || el.getAttribute('aria-hidden') === 'true') return false;
    return true;
  });
}

export interface DialogA11yOptions {
  /** Whether the dialog is currently displayed. */
  open: boolean;
  /** Called on Escape. */
  onClose: () => void;
  /**
   * Focused on open. Defaults to the first focusable element — a search
   * input should pass its own ref so typing starts immediately.
   */
  initialFocus?: React.RefObject<HTMLElement | null> | undefined;
}

export function useDialogA11y<T extends HTMLElement = HTMLDivElement>({
  open,
  onClose,
  initialFocus,
}: DialogA11yOptions) {
  const containerRef = useRef<T>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // Read through refs, not effect deps: a parent re-rendering with a new
  // inline `onClose` must not re-run the effect, whose cleanup would hand
  // focus back to the trigger and then steal it again — a blur the user
  // never caused, and a spurious error on any field validating on blur.
  const onCloseRef = useRef(onClose);
  const initialFocusRef = useRef(initialFocus);
  // Declared before the trap effect so the refs are fresh when it runs.
  useEffect(() => {
    onCloseRef.current = onClose;
    initialFocusRef.current = initialFocus;
  });

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    // Remembered BEFORE moving focus: after the move it is the dialog's own
    // input, and closing would return focus to itself.
    restoreRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const target =
      initialFocusRef.current?.current ?? focusableWithin(container)[0] ?? container;
    // `preventScroll` — focusing an input inside a dialog must not scroll the
    // page underneath, which moves content the user is not looking at.
    target.focus({ preventScroll: true });

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        // Consumed here: a page-level Escape handler must not also fire.
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !container) return;

      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        // Nothing to move to — keep focus on the dialog rather than let it
        // escape to the page behind.
        event.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !container.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !container.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore only if focus is still inside the dialog (or nowhere): if
      // the user has already clicked elsewhere, stealing it back would be
      // the second surprise.
      const active = document.activeElement;
      const stillInside =
        active === document.body || (container && container.contains(active));
      if (stillInside) restoreRef.current?.focus({ preventScroll: true });
    };
  }, [open]);

  return containerRef;
}
